import type { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import type { PaginationMeta } from '@core/http-types';
import type { EntityState } from '@core/ngrx-legacy';
import { createEntityCollectionStore, memoizeSelector, pipeState } from '@core/ngrx-legacy';
import type { PipelinePlacement, PipelineStage } from '@core/placements-types';
import type {
  PaginatedPipelineParams,
  PipelineParams,
  PipelineParamsWithStage,
  Stage,
} from '@core/stages-types';
import { uniqByKey } from '@core/utils-legacy';

import { EntityStoreKey } from '../../types';

export type StagePagination = Pick<PaginationMeta, 'page' | 'limit' | 'totalCount'>;

export interface PipelineStagesEntity {
  stages: PipelineStage[];
  id: string;
  pagination: Record<number, StagePagination>;
}

export interface PaginatedPlacementsPayload {
  offerSlug: string;
  stageId: number;
  placements: PipelinePlacement[];
  meta: PaginationMeta;
  mode: 'replace' | 'append';
}

const updatePlacementsInStage = (
  stage: PipelineStage,
  newPlacement: PipelinePlacement,
): PipelinePlacement[] => {
  let placementsUpdated = false;
  const newPlacements = stage.placements.flatMap(placement => {
    if (newPlacement.id === placement.id) {
      placementsUpdated = true;
      // placement deleted or moved to another stage
      if (newPlacement.stageId !== placement.stageId) {
        return [];
      }
      return newPlacement;
    } else {
      return placement;
    }
  });

  return placementsUpdated ? newPlacements : stage.placements;
};

const addPlacementToStage = (
  stage: PipelineStage,
  newPlacements: PipelinePlacement[],
  newPlacement: PipelinePlacement,
): PipelinePlacement[] =>
  [...newPlacements, newPlacement].sort((a, b) => {
    if (a.position === b.position) {
      return a.updatedAt < b.updatedAt ? -1 : 1;
    }

    return a.position < b.position ? 1 : -1;
  });

const updatePlacement = (
  state: EntityState<PipelineStagesEntity>,
  offerSlug: string,
  newPlacement: PipelinePlacement,
): PipelineStagesEntity => {
  const stages = state.entityMap[offerSlug]?.stages || [];
  return {
    id: offerSlug,
    pagination: state.entityMap[offerSlug]?.pagination ?? {},
    stages: stages.map(stage => {
      const updatedPlacements = updatePlacementsInStage(stage, newPlacement);
      const placementsUpdated = updatedPlacements !== stage.placements;

      if (!placementsUpdated && newPlacement.stageId === stage.id) {
        return {
          ...stage,
          placements: addPlacementToStage(stage, updatedPlacements, newPlacement),
        };
      }

      return {
        ...stage,
        placements: updatedPlacements,
      };
    }),
  };
};

const updateStage = (
  state: EntityState<PipelineStagesEntity>,
  offerSlug: string,
  stageId: number,
  newPlacements: PipelinePlacement[],
): PipelineStagesEntity => {
  const stages = state.entityMap[offerSlug]?.stages || [];
  return {
    id: offerSlug,
    pagination: state.entityMap[offerSlug]?.pagination ?? {},
    stages: stages.map(stage => {
      if (stage.id === stageId) {
        return { ...stage, placements: newPlacements };
      }

      return stage;
    }),
  };
};

const deletePlacements = (
  state: EntityState<PipelineStagesEntity>,
  payload: { offerSlug: string; candidateIds: number[] },
): PipelineStagesEntity => {
  const stages = state.entityMap[payload.offerSlug]?.stages!;
  const candidateIdsMap = Object.fromEntries(payload.candidateIds.map(id => [id, true]));

  return {
    id: payload.offerSlug,
    pagination: state.entityMap[payload.offerSlug]?.pagination ?? {},
    stages: stages.map(stage => ({
      ...stage,
      placements: stage.placements.filter(placement => !candidateIdsMap[placement.candidateId]),
    })),
  };
};

const seedStages = (
  state: EntityState<PipelineStagesEntity>,
  offerSlug: string,
  stages: Stage[],
): PipelineStagesEntity => {
  const existing = state.entityMap[offerSlug];
  const existingById = new Map((existing?.stages ?? []).map(stage => [stage.id, stage]));
  const incomingIds = new Set(stages.map(stage => stage.id));

  const seededStages: PipelineStage[] = stages.map(stage => {
    const prior = existingById.get(stage.id);
    return { ...stage, placements: prior?.placements ?? [] };
  });

  const retainedStages = (existing?.stages ?? []).filter(stage => !incomingIds.has(stage.id));

  const retainedPagination = Object.fromEntries(
    Object.entries(existing?.pagination ?? {}).filter(
      ([stageId]) => !incomingIds.has(Number(stageId)),
    ),
  );

  return {
    id: offerSlug,
    stages: [...seededStages, ...retainedStages],
    pagination: retainedPagination,
  };
};

const appendPlacements = (
  existing: PipelinePlacement[],
  incoming: PipelinePlacement[],
): PipelinePlacement[] => uniqByKey([...existing, ...incoming], 'id');

const adjustStageCount = (
  state: EntityState<PipelineStagesEntity>,
  { offerSlug, stageId, delta }: { offerSlug: string; stageId: number; delta: number },
): PipelineStagesEntity => {
  const existing = state.entityMap[offerSlug];
  const current = existing?.pagination?.[stageId];
  return {
    id: offerSlug,
    stages: existing?.stages ?? [],
    pagination: current
      ? {
          ...existing!.pagination,
          [stageId]: { ...current, totalCount: current.totalCount + delta },
        }
      : (existing?.pagination ?? {}),
  };
};

const applyPaginatedPlacements = (
  state: EntityState<PipelineStagesEntity>,
  { offerSlug, stageId, placements, meta, mode }: PaginatedPlacementsPayload,
): PipelineStagesEntity => {
  const existing = state.entityMap[offerSlug];
  const stages = existing?.stages ?? [];

  return {
    id: offerSlug,
    stages: stages.map(stage => {
      if (stage.id !== stageId) return stage;
      const nextPlacements =
        mode === 'append' ? appendPlacements(stage.placements, placements) : placements;
      return { ...stage, placements: nextPlacements };
    }),
    pagination: {
      ...(existing?.pagination ?? {}),
      [stageId]: { page: meta.page, limit: meta.limit, totalCount: meta.totalCount },
    },
  };
};

const {
  actions: defaultActions,
  reducer,
  namespace,
  selectors: { byId },
} = createEntityCollectionStore<PipelineStagesEntity>()(EntityStoreKey.PipelineStages, {
  customActions: adapter => ({
    fetchOne: (state, payload: { id: string; params: PipelineParams }) =>
      pipeState(state, next =>
        adapter.markAsPending(next, { scope: null, entities: [payload.id] }),
      ),
    updatePlacement: (
      state,
      payload: { offerId: number; offerSlug: string; candidateId: number; params: PipelineParams },
    ) => adapter.markAsPending(state, { scope: null, entities: [payload.offerSlug] }),
    updatePlacementSuccess: (state, payload: { offerSlug: string; data: PipelinePlacement }) => {
      const result = updatePlacement(state, payload.offerSlug, payload.data);
      return pipeState(
        state,
        next => adapter.updateOne(next, result),
        next => adapter.markAsLoaded(next, { scope: null, entities: [payload.offerSlug] }),
      );
    },
    updatePlacementFailed: (state, payload: { offerSlug: string; error: HttpErrorResponse }) =>
      adapter.setError(state, payload.error, { scope: null, entities: [payload.offerSlug] }),
    updateStagePlacements: (
      state,
      payload: { offerSlug: string; params: PipelineParamsWithStage },
    ) => adapter.markAsPending(state, { scope: null, entities: [payload.offerSlug] }),
    updateStagePlacementsSuccess: (
      state,
      payload: { offerSlug: string; stageId: number; placements: PipelinePlacement[] },
    ) => {
      const result = updateStage(state, payload.offerSlug, payload.stageId, payload.placements);
      return pipeState(
        state,
        next => adapter.updateOne(next, result),
        next => adapter.markAsLoaded(next, { scope: null, entities: [payload.offerSlug] }),
      );
    },
    updateStagePlacementsFailed: (
      state,
      payload: { offerSlug: string; error: HttpErrorResponse },
    ) => adapter.setError(state, payload.error, { scope: null, entities: [payload.offerSlug] }),
    deletePlacements: (state, payload: { offerSlug: string; candidateIds: number[] }) =>
      adapter.updateOne(state, deletePlacements(state, payload)),
    seedStages: (state, payload: { offerSlug: string; stages: Stage[] }) =>
      adapter.updateOne(state, seedStages(state, payload.offerSlug, payload.stages)),
    fetchStagePlacementsPaginated: (
      state,
      payload: { offerSlug: string; params: PaginatedPipelineParams },
    ) => adapter.markAsPending(state, { scope: null, entities: [payload.offerSlug] }),
    refreshLoadedStagePlacements: (
      state,
      payload: { offerSlug: string; stageId: number; params: PipelineParams },
    ) => state,
    fetchStagePlacementsPaginatedSuccess: (state, payload: PaginatedPlacementsPayload) => {
      const result = applyPaginatedPlacements(state, payload);
      return pipeState(
        state,
        next => adapter.updateOne(next, result),
        next => adapter.markAsLoaded(next, { scope: null, entities: [payload.offerSlug] }),
      );
    },
    fetchStagePlacementsPaginatedFailed: (
      state,
      payload: { offerSlug: string; stageId: number; error: HttpErrorResponse },
    ) => adapter.setError(state, payload.error, { scope: null, entities: [payload.offerSlug] }),
    adjustStageCount: (state, payload: { offerSlug: string; stageId: number; delta: number }) =>
      adapter.updateOne(state, adjustStageCount(state, payload)),
  }),
});

const actions = {
  fetchOne: defaultActions.fetchOne,
  fetchOneSuccess: defaultActions.fetchOneSuccess,
  fetchOneFailed: defaultActions.fetchOneFailed,
  updatePlacement: defaultActions.updatePlacement,
  updatePlacementSuccess: defaultActions.updatePlacementSuccess,
  updatePlacementFailed: defaultActions.updatePlacementFailed,
  updateStagePlacements: defaultActions.updateStagePlacements,
  updateStagePlacementsSuccess: defaultActions.updateStagePlacementsSuccess,
  updateStagePlacementsFailed: defaultActions.updateStagePlacementsFailed,
  deletePlacements: defaultActions.deletePlacements,
  seedStages: defaultActions.seedStages,
  fetchStagePlacementsPaginated: defaultActions.fetchStagePlacementsPaginated,
  fetchStagePlacementsPaginatedSuccess: defaultActions.fetchStagePlacementsPaginatedSuccess,
  fetchStagePlacementsPaginatedFailed: defaultActions.fetchStagePlacementsPaginatedFailed,
  refreshLoadedStagePlacements: defaultActions.refreshLoadedStagePlacements,
  adjustStageCount: defaultActions.adjustStageCount,
};
const pagination = memoizeSelector((offerSlug: string) =>
  createSelector(
    byId(offerSlug),
    (entity): Record<number, StagePagination> => entity?.pagination ?? {},
  ),
);
const PipelineStagesSelectors = { byId, pagination };

export {
  actions as PipelineStagesActions,
  reducer as PipelineStagesReducer,
  namespace as PipelineStagesNamespace,
  PipelineStagesSelectors,
};
